'use client';
import React, { useState, useMemo } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import FilterSidebar from './FilterSidebar';
import ExpertGrid from './ExpertGrid';
import { ExpertCardData } from '@/components/ui/ExpertCard';
import { BadgeTier } from '@/components/ui/BadgeTierChip';
import { VerificationStatus } from '@/components/ui/VerificationBadge';

export interface BrowseFilters {
  search: string;
  domains: string[];
  badgeTiers: BadgeTier[];
  rateMin: number;
  rateMax: number;
  verificationStatus: VerificationStatus[];
  experienceTiers: string[];
}

// Backend integration: GET /api/expert-profiles?is_live=true
const allExperts: ExpertCardData[] = [
  { id: 'expert-seed-001', pseudonym: 'Advisor #K9M', avatarSeed: 'Advisor #K9M', headline: 'Senior brand strategy and go-to-market leader with two decades of FMCG portfolio scaling across South Asia and Southeast Asia.', domainTags: ['FMCG', 'Go-to-Market', 'Brand Strategy', 'Portfolio Management'], badgeTier: 'GOLD', averageRating: 4.4, ratingCount: 18, hourlyRate: 22000, verificationStatus: 'WEB_CHECKED', experienceTier: 'PRINCIPAL' },
  { id: 'expert-seed-002', pseudonym: 'Advisor #R3T', avatarSeed: 'Advisor #R3T', headline: 'M&A and financial due diligence specialist with deep experience across financial services, insurance, and capital markets transactions.', domainTags: ['M&A', 'Due Diligence', 'Financial Services', 'Capital Markets'], badgeTier: 'SILVER', averageRating: 4.1, ratingCount: 7, hourlyRate: 35000, verificationStatus: 'WEB_CHECKED', experienceTier: 'EXPERT' },
  { id: 'expert-seed-003', pseudonym: 'Advisor #B6F', avatarSeed: 'Advisor #B6F', headline: 'Manufacturing and supply chain specialist focused on lean operations, cost reduction, and operational resilience in complex production environments.', domainTags: ['Manufacturing', 'Supply Chain', 'Lean Operations', 'Cost Optimisation'], badgeTier: 'BRONZE', averageRating: 3.7, ratingCount: 2, hourlyRate: 14000, verificationStatus: 'PENDING', experienceTier: 'SENIOR' },
  { id: 'expert-004', pseudonym: 'Advisor #H2P', avatarSeed: 'Advisor #H2P', headline: 'Healthcare policy and regulatory affairs expert with extensive experience in drug approvals, hospital accreditation, and public health system design.', domainTags: ['Healthcare', 'Regulatory Affairs', 'Public Health', 'Policy'], badgeTier: 'SILVER', averageRating: 4.3, ratingCount: 9, hourlyRate: 38000, verificationStatus: 'WEB_CHECKED', experienceTier: 'EXPERT' },
  { id: 'expert-005', pseudonym: 'Advisor #Q7Z', avatarSeed: 'Advisor #Q7Z', headline: 'Digital transformation and enterprise technology leader specialising in ERP implementations, data platform strategy, and IT-enabled operating model redesign.', domainTags: ['Digital Transformation', 'ERP', 'Data Strategy', 'Change Management'], badgeTier: 'GOLD', averageRating: 4.6, ratingCount: 22, hourlyRate: 45000, verificationStatus: 'BUYER_VERIFIED', experienceTier: 'PRINCIPAL' },
  { id: 'expert-006', pseudonym: 'Advisor #F5K', avatarSeed: 'Advisor #F5K', headline: 'Private equity and portfolio operations specialist with focus on value creation, operational due diligence, and post-acquisition integration.', domainTags: ['Private Equity', 'Portfolio Operations', 'Due Diligence', 'Value Creation'], badgeTier: 'VAULT', averageRating: 4.8, ratingCount: 34, hourlyRate: 65000, verificationStatus: 'BUYER_VERIFIED', experienceTier: 'EXPERT' },
  { id: 'expert-007', pseudonym: 'Advisor #N1W', avatarSeed: 'Advisor #N1W', headline: 'Logistics and last-mile delivery specialist with deep expertise in fleet operations, route optimisation, and third-party logistics contracting.', domainTags: ['Logistics', 'Last-Mile Delivery', 'Fleet Management', 'Supply Chain'], badgeTier: 'BRONZE', averageRating: 3.9, ratingCount: 3, hourlyRate: 16500, verificationStatus: 'PENDING', experienceTier: 'SENIOR' },
  { id: 'expert-008', pseudonym: 'Advisor #L8C', avatarSeed: 'Advisor #L8C', headline: 'Real estate capital markets and structured finance expert with experience across residential, commercial, and REIT transaction advisory.', domainTags: ['Real Estate', 'Capital Markets', 'REIT', 'Structured Finance'], badgeTier: 'SILVER', averageRating: 4.0, ratingCount: 6, hourlyRate: 52000, verificationStatus: 'WEB_CHECKED', experienceTier: 'EXPERT' },
  { id: 'expert-009', pseudonym: 'Advisor #D3V', avatarSeed: 'Advisor #D3V', headline: 'Consumer internet and D2C growth specialist with a track record of scaling digital-first brands from early traction to category leadership.', domainTags: ['D2C', 'Consumer Internet', 'Growth Strategy', 'Digital Marketing'], badgeTier: 'GOLD', averageRating: 4.5, ratingCount: 15, hourlyRate: 28000, verificationStatus: 'WEB_CHECKED', experienceTier: 'PRINCIPAL' },
];

export default function BrowseContent() {
  const [filters, setFilters] = useState<BrowseFilters>({
    search: '',
    domains: [],
    badgeTiers: [],
    rateMin: 0,
    rateMax: 100000,
    verificationStatus: [],
    experienceTiers: [],
  });
  const [sortBy, setSortBy] = useState<'rating' | 'rate_asc' | 'rate_desc' | 'recent'>('rating');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filtered = useMemo(() => {
    return allExperts
      .filter((e) => {
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!e.pseudonym.toLowerCase().includes(q) && !e.headline.toLowerCase().includes(q) && !e.domainTags.some((t) => t.toLowerCase().includes(q))) return false;
        }
        if (filters.domains.length > 0 && !filters.domains.some((d) => e.domainTags.includes(d))) return false;
        if (filters.badgeTiers.length > 0 && !filters.badgeTiers.includes(e.badgeTier)) return false;
        if (e.hourlyRate < filters.rateMin || e.hourlyRate > filters.rateMax) return false;
        if (filters.verificationStatus.length > 0 && !filters.verificationStatus.includes(e.verificationStatus)) return false;
        if (filters.experienceTiers.length > 0 && !filters.experienceTiers.includes(e.experienceTier)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'rating') return b.averageRating - a.averageRating;
        if (sortBy === 'rate_asc') return a.hourlyRate - b.hourlyRate;
        if (sortBy === 'rate_desc') return b.hourlyRate - a.hourlyRate;
        return 0;
      });
  }, [filters, sortBy]);

  const activeFilterCount = filters.domains.length + filters.badgeTiers.length + filters.verificationStatus.length + filters.experienceTiers.length;

  return (
    <div className="py-8">
      <div className="mb-8">
        <p className="text-xs font-mono-data text-primary uppercase tracking-widest mb-2">Anonymous Expert Network</p>
        <h1 className="font-heading text-display text-foreground mb-2">Browse Experts</h1>
        <p className="text-muted-foreground text-sm">
          {allExperts.length} verified experts available. All identities masked.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search by domain, keyword, or pseudonym…"
            className="w-full bg-input border border-border rounded-lg pl-8 pr-4 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors"
            aria-label="Search experts"
          />
          {filters.search && (
            <button onClick={() => setFilters((f) => ({ ...f, search: '' }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all duration-150 ${sidebarOpen || activeFilterCount > 0 ? 'border-primary/40 bg-primary/8 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/30'}`}
          aria-label="Toggle filters"
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-mono-data flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 transition-colors cursor-pointer"
          aria-label="Sort experts"
        >
          <option value="rating">Sort: Highest Rated</option>
          <option value="rate_asc">Sort: Rate Low to High</option>
          <option value="rate_desc">Sort: Rate High to Low</option>
          <option value="recent">Sort: Recently Added</option>
        </select>

        <span className="text-xs text-muted-foreground font-mono-data ml-auto">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="flex gap-6">
        {sidebarOpen && (
          <div className="w-64 flex-shrink-0">
            <FilterSidebar filters={filters} onChange={setFilters} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <ExpertGrid experts={filtered} />
        </div>
      </div>
    </div>
  );
}